import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LogOut, GraduationCap, Compass, Radio, LayoutDashboard, KeyRound, Award,
  BookOpenCheck, Users, Scale, FileText, Menu, X, ShieldAlert, ShieldCheck, Building2
} from "lucide-react";

const PUBLIC_LINKS = [
  { to: "/education",    label: "Education",    icon: BookOpenCheck, testid: "nav-education" },
  { to: "/courses",      label: "Programmes",   icon: Compass,       testid: "nav-courses" },
  { to: "/instructors",  label: "Instructors",  icon: Users,         testid: "nav-instructors" },
  { to: "/accreditation", label: "Accreditation", icon: ShieldCheck, testid: "nav-accreditation" },
  { to: "/organisations", label: "For Organisations", icon: Building2, testid: "nav-organisations" },
  { to: "/policies",     label: "Policies",     icon: Scale,         testid: "nav-policies" },
  { to: "/publications", label: "Publications", icon: FileText,      testid: "nav-publications" },
  { to: "/live",         label: "Live",         icon: Radio,         testid: "nav-live" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const linkCls = ({ isActive }) =>
    `px-2.5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
      isActive ? "bg-[var(--ink)] text-white" : "text-[var(--ink)] hover:bg-[var(--muted-surface)]"
    }`;

  const mobileLinkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-base font-semibold border transition-colors ${
      isActive
        ? "bg-[var(--navy)] text-[var(--gold-soft)] border-[var(--navy)]"
        : "bg-white text-[var(--ink)] border-[var(--border-warm)] hover:bg-[var(--muted-surface)]"
    }`;

  return (
    <header className="sticky top-0 z-40 border-b border-[var(--border-warm)] bg-[rgba(249,247,242,0.88)] backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-logo" aria-label="Converged Security Institute - home">
          <img src="/csi-logo.jpg" alt="Converged Security Institute" className="h-10 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-1" aria-label="Primary">
          {PUBLIC_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={l.testid}>
              <span className="inline-flex items-center gap-1.5"><l.icon size={15}/>{l.label}</span>
            </NavLink>
          ))}
          {user && (
            <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">
              <span className="inline-flex items-center gap-1.5"><LayoutDashboard size={15}/>Dashboard</span>
            </NavLink>
          )}
          {user && (
            <NavLink to="/cpd" className={linkCls} data-testid="nav-cpd">
              <span className="inline-flex items-center gap-1.5"><Award size={15}/>CPD</span>
            </NavLink>
          )}
          {user && user.role === "instructor" && (
            <NavLink to="/teach" className={linkCls} data-testid="nav-teach">
              <span className="inline-flex items-center gap-1.5"><GraduationCap size={15}/>Teach</span>
            </NavLink>
          )}
          {user && user.role === "admin" && (
            <NavLink to="/admin" className={linkCls} data-testid="nav-admin">
              <span className="inline-flex items-center gap-1.5"><ShieldAlert size={15}/>Admin</span>
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {!user ? (
            <>
              <Link to="/login" className="chip hidden sm:inline-flex" data-testid="nav-login">Log in</Link>
              <Link to="/register" className="btn-terracotta px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap" data-testid="nav-register">
                Join CSI
              </Link>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex flex-col items-end leading-tight">
                <Link to="/account" title="My account" className="text-sm font-semibold whitespace-nowrap max-w-[160px] truncate hover:underline" data-testid="nav-user-name">{user.name}</Link>
                <span className="text-[11px] tracking-widest uppercase text-amber-700 font-bold">{user.role}</span>
              </div>
              <button
                onClick={async () => { if (window.__examLeaveGuard) { window.__examLeaveGuard(); return; } await logout(); nav("/"); }}
                className="chip inline-flex items-center gap-1.5"
                data-testid="nav-logout-btn"
                title="Log out"
                aria-label="Log out"
              >
                <LogOut size={14}/> <span className="hidden sm:inline">Log out</span>
              </button>
            </div>
          )}

          {/* Mobile menu toggle */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[var(--border-warm)] bg-white text-[var(--navy)]"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={18}/> : <Menu size={18}/>}
          </button>
        </div>
      </div>

      {/* Mobile drawer - rendered into document.body to escape the sticky header's backdrop-filter stacking context */}
      {open && createPortal(
        <>
          <div className="xl:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]" aria-hidden="true" onClick={() => setOpen(false)}/>
          <div
            id="mobile-menu"
            className="xl:hidden fixed top-16 inset-x-0 bottom-0 z-[70] overflow-y-auto bg-[var(--muted-surface)] border-t border-[var(--border-warm)]"
            role="dialog"
            aria-modal="true"
            aria-label="Primary navigation"
            data-testid="nav-mobile-drawer"
          >
            <nav className="max-w-7xl mx-auto px-4 py-5 space-y-2.5" aria-label="Mobile primary">
              {PUBLIC_LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} className={mobileLinkCls} data-testid={`mobile-${l.testid}`}>
                  <l.icon size={18} className="shrink-0"/> {l.label}
                </NavLink>
              ))}
              {user && (
                <>
                  <NavLink to="/dashboard" className={mobileLinkCls} data-testid="mobile-nav-dashboard">
                    <LayoutDashboard size={18}/> Dashboard
                  </NavLink>
                  <NavLink to="/cpd" className={mobileLinkCls} data-testid="mobile-nav-cpd">
                    <Award size={18}/> CPD
                  </NavLink>
                  <NavLink to="/account" className={mobileLinkCls} data-testid="mobile-nav-account">
                    <KeyRound size={18}/> My account
                  </NavLink>
                  {user.role === "instructor" && (
                    <NavLink to="/teach" className={mobileLinkCls} data-testid="mobile-nav-teach">
                      <GraduationCap size={18}/> Teach
                    </NavLink>
                  )}
                  {user.role === "admin" && (
                    <NavLink to="/admin" className={mobileLinkCls} data-testid="mobile-nav-admin">
                      <ShieldAlert size={18}/> Admin
                    </NavLink>
                  )}
                </>
              )}
              {!user && (
                <div className="pt-2 flex gap-2">
                  <Link to="/login" className="chip flex-1 justify-center" data-testid="mobile-nav-login">Log in</Link>
                  <Link to="/register" className="btn-terracotta flex-1 py-2.5 rounded-full text-sm font-semibold text-center" data-testid="mobile-nav-register">
                    Join CSI
                  </Link>
                </div>
              )}
            </nav>
          </div>
        </>,
        document.body
      )}
    </header>
  );
}
